import type { usePaginationState } from '../pagination/usePaginationState'
import { useUpdateUrlQuery } from '~/composables/useUpdateUrlQuery'

type FilterValues = Record<string, string>
type ToRefresh = Ref<
  Record<
    string,
    {
      refresh: () => void
      ignoredProperties?: string[]
    }
  >
>

export function useFilterState({
  paginationState,
}: {
  paginationState: ReturnType<typeof usePaginationState>
}) {
  const updateUrlQuery = useUpdateUrlQuery()
  const initialFilterValues = updateUrlQuery.getQueryByKey('filter')
  const filterValues = ref({ ...initialFilterValues }) as Ref<FilterValues>
  const lastFilterValues = ref({ ...initialFilterValues }) as Ref<FilterValues>
  const toRefresh = ref({}) as ToRefresh

  function updateValue(key: string, value: any) {
    filterValues.value[key] = value
  }

  function deleteValue(key: string) {
    delete filterValues.value[key]
  }

  function addRefreshFunction(
    key: string,
    refresh: () => void,
    ignoredProperties?: string[]
  ) {
    toRefresh.value[key] = {
      refresh,
      ignoredProperties,
    }
  }

  function deleteRefreshFunction(key: string) {
    delete toRefresh.value[key]
  }

  function getChangedPropertiesKeys(
    lastValues: FilterValues,
    currentValues: FilterValues
  ) {
    return [...Object.keys(currentValues), ...Object.keys(lastValues)].reduce(
      (acc, key) => {
        if (lastValues[key] !== currentValues[key] && !acc.includes(key)) {
          acc.push(key)
        }

        return acc
      },
      [] as string[]
    )
  }

  let submitFilterTimeout: ReturnType<typeof setTimeout> | null = null
  function submitFilter() {
    if (submitFilterTimeout) {
      clearTimeout(submitFilterTimeout)
    }

    submitFilterTimeout = setTimeout(() => {
      const listingsToRefresh = []

      for (const key in toRefresh.value) {
        const toRefreshCurrent = toRefresh.value[key]
        const changedPropertiesKeys = getChangedPropertiesKeys(
          lastFilterValues.value,
          filterValues.value
        )

        if (
          toRefreshCurrent.ignoredProperties &&
          changedPropertiesKeys.filter((changedKey) => {
            return !toRefreshCurrent.ignoredProperties?.includes(changedKey)
          }).length === 0
        ) {
          continue
        }

        if (changedPropertiesKeys.length === 0) {
          continue
        }

        listingsToRefresh.push(toRefreshCurrent)
      }

      updateUrlQuery.setQueryByKey({ key: 'filter', value: filterValues.value })
      paginationState.actions.setPage(1, false)

      for (const toRefreshCurrent of listingsToRefresh) {
        toRefreshCurrent.refresh()
      }

      lastFilterValues.value = { ...filterValues.value }
    }, 50)
  }

  // watch url query params
  watch(
    () => updateUrlQuery.getQueryByKey('filter'),
    (newFilterValues: FilterValues | null, oldFilterValues: FilterValues | null) => {
      const changedKeys = getChangedPropertiesKeys(
        oldFilterValues || {},
        newFilterValues || {}
      )

      if (!changedKeys.length) {
        return
      }

      lastFilterValues.value = { ...filterValues.value }
      filterValues.value = newFilterValues || {}

      submitFilter()
    }
  )

  function resetFilter() {
    updateUrlQuery.deleteQueryByKey('filter')

    filterValues.value = {}
    lastFilterValues.value = {}

    for (const key in toRefresh.value) {
      const toRefreshCurrent = toRefresh.value[key]
      toRefreshCurrent.refresh()
    }
  }

  return {
    filterValues,
    actions: {
      addRefreshFunction,
      deleteRefreshFunction,
      updateValue,
      deleteValue,
      submitFilter,
      resetFilter,
    },
  }
}
